import { VMultiSelect, VSelectOptionWithDescription } from '@vision/ui/components';
import { ReportsDetailGraphicSchemaValues, SimpleFlowQuestion } from '@vision/ui/interfaces';
import { getBodyText } from '@vision/ui/utils';
import { FormikContextType } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface MatrixQuestionStatementItem extends SimpleFlowQuestion {
  parent_matrix?: {
    id: string;
  } | null;
}

export interface MatrixFlowQuestionItem extends SimpleFlowQuestion {
  statements?: MatrixQuestionStatementItem[];
}

export interface MatrixQuestionStatementOption {
  value: string;
  label: string;
  description?: string;
}

interface BuildMatrixQuestionStatementOptionsParams {
  selectedQuestionIds: string[];
  flowQuestions: MatrixFlowQuestionItem[];
  language: string;
  textWithNativeEmoji: (value: string) => string;
}

interface ReportsDetailGraphicCompareMatrixQuestionStatementsSelectProps {
  id: string;
  dataTestId: string;
  data: MatrixQuestionStatementOption[];
  value: string[];
  onBlur: (...args: any[]) => void;
  onChange: (values: string[]) => void;
}

export function buildMatrixQuestionStatementOptions({
  selectedQuestionIds,
  flowQuestions,
  language,
  textWithNativeEmoji,
}: BuildMatrixQuestionStatementOptionsParams): MatrixQuestionStatementOption[] {
  if (!selectedQuestionIds.length || !flowQuestions.length) {
    return [];
  }

  const selectedQuestionIdSet = new Set(selectedQuestionIds);

  return flowQuestions.flatMap((question) => {
    if (!selectedQuestionIdSet.has(question.id)) {
      return [];
    }

    const statements = Array.isArray(question.statements) ? question.statements : [];
    if (!statements.length) {
      return [];
    }

    const parentLabel = textWithNativeEmoji(getBodyText(question.body, language) || question.key || question.id);

    return statements
      .map((statement) => ({
        value: `${question.id}:${statement.id}`,
        label: textWithNativeEmoji(getBodyText(statement.body, language) || statement.key || statement.id),
        description: parentLabel,
      }))
      .filter((item) => !!item.value);
  });
}

export function hasSelectedQuestionWithStatements(
  selectedQuestionIds: string[],
  flowQuestions: MatrixFlowQuestionItem[],
): boolean {
  if (!selectedQuestionIds.length || !flowQuestions.length) {
    return false;
  }

  const selectedQuestionIdSet = new Set(selectedQuestionIds);

  return flowQuestions.some((question) => {
    if (!selectedQuestionIdSet.has(question.id)) {
      return false;
    }

    return Array.isArray(question.statements) && question.statements.length > 0;
  });
}

export function normalizeMatrixQuestionStatementValues(
  selectedValues: string[],
  matrixQuestionStatements: MatrixQuestionStatementOption[],
): string[] {
  if (!selectedValues.length || !matrixQuestionStatements.length) {
    return selectedValues;
  }

  const statementIdToCompositeValue = matrixQuestionStatements.reduce<Record<string, string>>((acc, item) => {
    const [questionId, statementId] = item.value.split(':');
    if (questionId && statementId) {
      acc[statementId] = item.value;
    }
    return acc;
  }, {});

  return selectedValues.map((value) => {
    if (value.includes(':')) {
      return value;
    }

    return statementIdToCompositeValue[value] || value;
  });
}

interface UseReportsDetailGraphicCompareMatrixQuestionStatementsParams {
  formik: FormikContextType<ReportsDetailGraphicSchemaValues>;
  selectedFlowIds: string[];
  selectedQuestionIds: string[];
  hasSelectedQuestionWithStatements: boolean;
  matrixQuestionStatements: MatrixQuestionStatementOption[];
}

export function useReportsDetailGraphicCompareMatrixQuestionStatements({
  formik,
  selectedFlowIds,
  selectedQuestionIds,
  hasSelectedQuestionWithStatements,
  matrixQuestionStatements,
}: UseReportsDetailGraphicCompareMatrixQuestionStatementsParams) {
  const selectedMatrixQuestionStatements =
    formik.values.selectedCompareCriteria.selectedMatrixQuestionStatementIds || [];

  useEffect(() => {
    if (!selectedMatrixQuestionStatements.length || !matrixQuestionStatements.length) {
      return;
    }

    const normalizedValues = normalizeMatrixQuestionStatementValues(
      selectedMatrixQuestionStatements,
      matrixQuestionStatements,
    );

    const hasAnyChange = normalizedValues.some((value, index) => value !== selectedMatrixQuestionStatements[index]);

    if (hasAnyChange) {
      formik.setFieldValue('selectedCompareCriteria.selectedMatrixQuestionStatementIds', normalizedValues);
    }
  }, [selectedMatrixQuestionStatements, matrixQuestionStatements, formik]);

  const shouldShowMatrixQuestionStatements = useMemo(() => {
    return selectedFlowIds.length > 0 && selectedQuestionIds.length > 0 && hasSelectedQuestionWithStatements;
  }, [selectedFlowIds, selectedQuestionIds, hasSelectedQuestionWithStatements]);

  const handleChangeMatrixQuestionStatements = (values: string[]) => {
    formik.setFieldValue('selectedCompareCriteria.selectedMatrixQuestionStatementIds', values);
  };

  return {
    selectedMatrixQuestionStatements,
    shouldShowMatrixQuestionStatements,
    handleChangeMatrixQuestionStatements,
  };
}

export function ReportsDetailGraphicCompareMatrixQuestionStatementsSelect({
  id,
  dataTestId,
  data,
  value,
  onBlur,
  onChange,
}: ReportsDetailGraphicCompareMatrixQuestionStatementsSelectProps) {
  const { t } = useTranslation();

  return (
    <VMultiSelect
      id={id}
      data-testid={dataTestId}
      label={t('page-reports:matrixSubQuestions')}
      data={data}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      searchable={true}
      clearable={true}
      withSelectAll={false}
      showAllSelectedPill={false}
      itemComponent={VSelectOptionWithDescription}
      withAsterisk={false}
    />
  );
}
